import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Search, Plus, Minus, Trash2, Loader2, Package, MapPin, Scale, Box, Layers, PackagePlus, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axiosInstance from '../utils/axiosInstance';

const parenPart = (name) => {
  const m = /\(([^)]*)\)\s*$/.exec(name || '');
  return m ? m[1].trim() : '';
};
const baseProductName = (name) => ((name || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || name);

/** Group raw Boxopedia rows by VARIANT (shopify_variant_id or product_id). */
export const groupSearchResults = (rows) => {
  const groups = {};
  for (const row of rows) {
    const key = row.shopify_variant_id || `pid-${row.product_id}`;
    if (!groups[key]) {
      groups[key] = {
        key,
        product_name: row.product_name,
        product_sku: row.product_sku,
        shopify_variant_id: row.shopify_variant_id,
        shopify_template_id: row.shopify_template_id,
        product_id: row.product_id,
        shipped_on_pallet: row.shipped_on_pallet,
        fragile: row.fragile,
        variant_title: row.variant_title || null,
        image_url: row.image_url || null,
        boxes: [],
      };
    }
    groups[key].boxes.push({
      id: row.id, box_number: row.box_number,
      width: row.width, height: row.height, length: row.length, weight: row.weight || 0,
    });
  }
  for (const g of Object.values(groups)) {
    g.boxes.sort((a, b) => (a.box_number || 0) - (b.box_number || 0));
    g.total_weight = g.boxes.reduce((s, b) => s + (b.weight || 0), 0);
    g.total_volume = g.boxes.reduce((s, b) => s + (b.width || 0) * (b.height || 0) * (b.length || 0), 0);
    const largest = g.boxes.reduce((best, b) => {
      const vol = (b.width || 0) * (b.height || 0) * (b.length || 0);
      return vol > best.vol ? { ...b, vol } : best;
    }, { vol: 0 });
    g.display_dims = largest;
    g.variant_label = g.variant_title || parenPart(g.product_name) || '';
    g.base_name = baseProductName(g.product_name);
  }
  return Object.values(groups);
};

/** Group VARIANT groups by PRODUCT (shopify_template_id → sku → base name). */
export const groupByProduct = (variantGroups) => {
  const prods = {};
  for (const v of variantGroups) {
    const pkey = v.shopify_template_id ? `tpl-${v.shopify_template_id}`
      : (v.product_sku ? `sku-${v.product_sku}` : `nm-${v.base_name}`);
    if (!prods[pkey]) prods[pkey] = { key: pkey, base_name: v.base_name, sku: v.product_sku, image_url: null, variants: [] };
    if (!prods[pkey].image_url && v.image_url) prods[pkey].image_url = v.image_url;
    prods[pkey].variants.push(v);
  }
  for (const p of Object.values(prods)) {
    p.variants.sort((a, b) => (a.variant_label || '').localeCompare(b.variant_label || ''));
    const dimKeys = new Set(p.variants.map(v => `${v.display_dims.width}x${v.display_dims.height}x${v.display_dims.length}`));
    const wKeys = new Set(p.variants.map(v => (v.total_weight || 0).toFixed(2)));
    p.uniform = dimKeys.size === 1 && wKeys.size === 1;
    p.rep = p.variants[0];
  }
  return Object.values(prods);
};

const litres = (cm3) => (cm3 || 0) / 1000;
const fmtL = (cm3) => litres(cm3).toLocaleString(undefined, { maximumFractionDigits: 1 });
const boxVolume = (b) => (b.width || 0) * (b.height || 0) * (b.length || 0);
const productVolume = (p) => p.packaging_boxes
  ? p.packaging_boxes.reduce((s, b) => s + boxVolume(b), 0)
  : (p.dimensions.width || 0) * (p.dimensions.height || 0) * (p.dimensions.length || 0);

const Badge = ({ color, children }) => (
  <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${color}`}>{children}</span>
);

const Thumb = ({ src, size = 44 }) => {
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [src]);
  if (!src || err) {
    return (
      <div style={{ width: size, height: size }} className="rounded-md bg-muted border border-border flex items-center justify-center shrink-0">
        <Package className="h-4 w-4 text-muted-foreground/40" />
      </div>
    );
  }
  return <img src={src} onError={() => setErr(true)} style={{ width: size, height: size }}
    className="rounded-md object-cover bg-muted border border-border shrink-0" alt="" loading="lazy" />;
};

const DimChips = ({ dims, weight, volume }) => (
  <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
    <Badge color="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"><Box className="h-3 w-3" />{dims}</Badge>
    <Badge color="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"><Scale className="h-3 w-3" />{weight} kg</Badge>
    <Badge color="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"><Layers className="h-3 w-3" />{volume} L</Badge>
  </div>
);

const CustomOrderModal = ({ open, onClose, onSimulate }) => {
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [rawSearchResults, setRawSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const [manualOpen, setManualOpen] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualDims, setManualDims] = useState({ w: '', h: '', l: '', weight: '', qty: '1' });

  const [products, setProducts] = useState([]);
  const [justAdded, setJustAdded] = useState(null);
  const searchRef = useRef(null);

  const productGroups = useMemo(() => groupByProduct(groupSearchResults(rawSearchResults)), [rawSearchResults]);
  const addedKeys = useMemo(() => new Set(products.map(p => p.id)), [products]);

  useEffect(() => {
    if (open && countries.length === 0) fetchCountries();
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const fetchCountries = async () => {
    setCountriesLoading(true);
    try {
      const response = await axiosInstance.get('api/shipping-rates/countries/');
      if (response.data.success) {
        setCountries(response.data.countries.filter(c => c.is_active).sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (err) { console.error('Failed to fetch countries', err); }
    finally { setCountriesLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    const q = searchQuery.trim();
    if (q.length < 2) { setRawSearchResults([]); setSearched(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const response = await axiosInstance.get('api/packaging-data/', {
          params: { search: q, page_size: 80, has_dimensions: 'true' },
        });
        setRawSearchResults(response.data.data || []);
      } catch (err) { console.error('Search failed', err); setRawSearchResults([]); }
      finally { setSearching(false); setSearched(true); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, open]);

  const flash = (id) => { setJustAdded(id); setTimeout(() => setJustAdded(v => (v === id ? null : v)), 600); };
  const toggleExpand = (key) => setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const addVariant = (v, image_url) => {
    const pid = `box-${v.key}`;
    flash(pid);
    const idx = products.findIndex(p => p.id === pid);
    if (idx >= 0) { setProducts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: p.quantity + 1 } : p)); return; }
    const isMultiBox = v.boxes.length > 1;
    setProducts(prev => [...prev, {
      id: pid,
      variant_id: v.shopify_variant_id || pid,
      title: v.product_name,
      variant_title: v.variant_label || '',
      sku: v.product_sku || '',
      quantity: 1, unit_price: 0, cost: 0,
      weight: v.total_weight,
      dimensions: { width: v.display_dims.width, height: v.display_dims.height, length: v.display_dims.length },
      shipped_on_pallet: v.shipped_on_pallet || false,
      fragile: v.fragile || false,
      image_url: v.image_url || image_url || null,
      ...(isMultiBox ? { packaging_boxes: v.boxes.map(b => ({ width: b.width, height: b.height, length: b.length, weight: b.weight || 0 })) } : {}),
    }]);
  };
  // Back-compat name used elsewhere.
  const addFromBoxopedia = (v) => addVariant(v, v.image_url);

  const addManualProduct = () => {
    const w = parseFloat(manualDims.w), h = parseFloat(manualDims.h), l = parseFloat(manualDims.l);
    const wt = parseFloat(manualDims.weight), qty = parseInt(manualDims.qty) || 1;
    if (!manualName.trim() || isNaN(w) || isNaN(h) || isNaN(l) || w <= 0 || h <= 0 || l <= 0) return;
    const id = `manual-${Date.now()}`;
    flash(id);
    setProducts(prev => [...prev, {
      id, variant_id: id, title: manualName.trim(), variant_title: '', sku: '',
      quantity: qty, unit_price: 0, cost: 0, weight: isNaN(wt) ? 0 : wt,
      dimensions: { width: w, height: h, length: l }, image_url: null, manual: true,
    }]);
    setManualName('');
    setManualDims({ w: '', h: '', l: '', weight: '', qty: '1' });
  };

  const removeProduct = (id) => setProducts(prev => prev.filter(p => p.id !== id));
  const updateQuantity = (id, delta) => setProducts(prev => prev.map(p => p.id === id ? { ...p, quantity: Math.max(1, p.quantity + delta) } : p));
  const setQuantity = (id, value) => { const n = parseInt(value); if (!isNaN(n) && n >= 1) setProducts(prev => prev.map(p => p.id === id ? { ...p, quantity: n } : p)); };

  const handleSimulate = () => { if (selectedCountry && products.length > 0) onSimulate(products, selectedCountry, postalCode); };

  if (!open) return null;

  const totalItems = products.reduce((s, p) => s + p.quantity * (p.packaging_boxes ? p.packaging_boxes.length : 1), 0);
  const totalWeight = products.reduce((s, p) => s + (p.weight || 0) * p.quantity, 0);
  const totalVolume = products.reduce((s, p) => s + productVolume(p) * p.quantity, 0);
  const euCountries = countries.filter(c => c.is_eu);
  const nonEuCountries = countries.filter(c => !c.is_eu);
  const manualValid = manualName.trim() && manualDims.w && manualDims.h && manualDims.l;
  const ready = selectedCountry && products.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border rounded-2xl shadow-2xl w-full max-w-5xl h-[88vh] overflow-hidden flex flex-col">
        {/* Header + destination */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2"><PackagePlus className="h-5 w-5 text-primary" /></div>
            <div>
              <h2 className="text-lg font-semibold leading-tight">Custom Order Builder</h2>
              <p className="text-xs text-muted-foreground">Build a fictive cart and simulate the packing &amp; carrier</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2 py-1">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)}
                className="bg-transparent text-sm outline-none max-w-[170px] py-1" disabled={countriesLoading}>
                <option value="">{countriesLoading ? 'Loading…' : 'Destination…'}</option>
                {euCountries.length > 0 && <optgroup label="EU">{euCountries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</optgroup>}
                {nonEuCountries.length > 0 && <optgroup label="Non-EU">{nonEuCountries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</optgroup>}
              </select>
              <Input placeholder="Postcode" value={postalCode} onChange={(e) => setPostalCode(e.target.value)}
                className="w-24 h-7 border-0 bg-transparent px-1 focus-visible:ring-0" />
            </div>
            <button onClick={onClose} className="rounded-lg p-2 hover:bg-muted"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Two-pane body */}
        <div className="flex-1 grid md:grid-cols-2 min-h-0 divide-y md:divide-y-0 md:divide-x divide-border">
          {/* LEFT — add products */}
          <div className="flex flex-col min-h-0">
            <div className="px-5 pt-4 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
                <input ref={searchRef} placeholder="Search Boxopedia by product name or SKU…" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background pl-9 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-muted-foreground">
                  {searchQuery.trim().length >= 2
                    ? (searching ? 'Searching…' : `${productGroups.length} product${productGroups.length !== 1 ? 's' : ''}`)
                    : 'Type at least 2 characters'}
                </span>
                <button onClick={() => setManualOpen(o => !o)} className={`text-[11px] inline-flex items-center gap-1 rounded px-2 py-1 border ${manualOpen ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-muted/50'}`}>
                  <Plus className="h-3 w-3" /> Manual entry
                </button>
              </div>
            </div>

            {manualOpen && (
              <div className="mx-5 mb-2 rounded-lg border border-border bg-muted/20 p-3 space-y-2">
                <Input placeholder="Product name" value={manualName} onChange={(e) => setManualName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && manualValid && addManualProduct()} className="text-sm" />
                <div className="flex items-end gap-1.5">
                  {[['w', 'W'], ['h', 'H'], ['l', 'L']].map(([k, label]) => (
                    <label key={k} className="flex-1">
                      <span className="block text-[10px] text-muted-foreground mb-0.5">{label} (cm)</span>
                      <input type="number" value={manualDims[k]} onChange={(e) => setManualDims(d => ({ ...d, [k]: e.target.value }))}
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-center" />
                    </label>
                  ))}
                  <label className="w-16"><span className="block text-[10px] text-muted-foreground mb-0.5">kg</span>
                    <input type="number" value={manualDims.weight} onChange={(e) => setManualDims(d => ({ ...d, weight: e.target.value }))}
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-center" /></label>
                  <label className="w-14"><span className="block text-[10px] text-muted-foreground mb-0.5">Qty</span>
                    <input type="number" value={manualDims.qty} onChange={(e) => setManualDims(d => ({ ...d, qty: e.target.value }))}
                      className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-center" /></label>
                  <Button size="sm" className="h-9 shrink-0" onClick={addManualProduct} disabled={!manualValid}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* Search results — grouped by product */}
            <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-2">
              {searchQuery.trim().length < 2 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-2 py-10">
                  <Search className="h-8 w-8 opacity-30" />
                  <p className="text-sm">Search the Boxopedia to add real products,<br />or use manual entry for a quick box.</p>
                </div>
              ) : searched && productGroups.length === 0 && !searching ? (
                <div className="text-center text-sm text-muted-foreground py-10">No products found for “{searchQuery}”. Try another term or add manually.</div>
              ) : (
                productGroups.map(prod => {
                  const single = prod.variants.length === 1;
                  const v0 = prod.rep;
                  const isOpen = expanded.has(prod.key);
                  const addedCount = prod.variants.filter(v => addedKeys.has(`box-${v.key}`)).length;
                  const dimsLabel = prod.uniform
                    ? `${v0.display_dims.width}×${v0.display_dims.height}×${v0.display_dims.length} cm`
                    : 'dims vary';
                  return (
                    <div key={prod.key} className={`rounded-lg border ${addedCount ? 'border-primary/40' : 'border-border'}`}>
                      {/* Product header */}
                      <div
                        role="button"
                        onClick={() => single ? addVariant(v0, prod.image_url) : toggleExpand(prod.key)}
                        className="w-full text-left p-3 flex items-start gap-3 cursor-pointer hover:bg-muted/40 rounded-lg"
                      >
                        <Thumb src={prod.image_url} size={48} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm leading-tight">{prod.base_name}</p>
                            {single ? (
                              <span className={`shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${addedCount ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                                <Plus className="h-3.5 w-3.5" /> {addedCount ? 'Add more' : 'Add'}
                              </span>
                            ) : (
                              <span className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground">
                                {addedCount > 0 && <Badge color="bg-primary text-primary-foreground">{addedCount} in cart</Badge>}
                                {prod.variants.length} variants
                                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {prod.sku && <span className="text-[11px] text-muted-foreground">{prod.sku}</span>}
                            {single && <DimChips dims={`${v0.display_dims.width}×${v0.display_dims.height}×${v0.display_dims.length} cm`} weight={(v0.total_weight || 0).toFixed(2)} volume={fmtL(v0.total_volume)} />}
                            {!single && <span className="text-[11px] text-muted-foreground">{dimsLabel}</span>}
                            {v0.shipped_on_pallet && <Badge color="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">Pallet</Badge>}
                            {v0.fragile && <Badge color="bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">Fragile</Badge>}
                          </div>
                        </div>
                      </div>
                      {/* Variants (expanded) */}
                      {!single && isOpen && (
                        <div className="border-t border-border/60 divide-y divide-border/40">
                          {prod.variants.map(v => {
                            const added = addedKeys.has(`box-${v.key}`);
                            return (
                              <button key={v.key} onClick={() => addVariant(v, prod.image_url)}
                                className="w-full text-left px-3 py-2 pl-4 flex items-center gap-3 hover:bg-muted/40">
                                <Thumb src={v.image_url || prod.image_url} size={32} />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium truncate">{v.variant_label || 'Default'}</p>
                                  <p className="text-[10px] text-muted-foreground">
                                    {v.boxes.length > 1 ? `${v.boxes.length} boxes · ` : `${v.display_dims.width}×${v.display_dims.height}×${v.display_dims.length} cm · `}
                                    {(v.total_weight || 0).toFixed(2)} kg · {fmtL(v.total_volume)} L
                                  </p>
                                </div>
                                <span className={`shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${added ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                                  <Plus className="h-3 w-3" /> {added ? 'More' : 'Add'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT — the order */}
          <div className="flex flex-col min-h-0 bg-muted/10">
            <div className="px-5 pt-4 pb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><Package className="h-4 w-4 text-primary" /> Your order</h3>
              {products.length > 0 && (
                <button onClick={() => setProducts([])} className="text-[11px] text-muted-foreground hover:text-red-500 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Clear</button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-2">
              {products.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-2 py-10">
                  <div className="rounded-full border-2 border-dashed border-border p-4"><Package className="h-7 w-7 opacity-30" /></div>
                  <p className="text-sm">Nothing added yet.<br />Add products from the left to build the cart.</p>
                </div>
              ) : (
                products.map(p => {
                  const boxes = p.packaging_boxes;
                  const flashing = justAdded === p.id;
                  return (
                    <div key={p.id} className={`rounded-lg border bg-card p-3 transition-shadow ${flashing ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}>
                      <div className="flex items-start gap-3">
                        <Thumb src={p.image_url} size={40} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm leading-tight">{p.title}</p>
                            <button onClick={() => removeProduct(p.id)} className="rounded p-1 text-muted-foreground hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-500 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                          <div className="flex flex-wrap items-center gap-1 mt-0.5">
                            {p.sku && <span className="text-[11px] text-muted-foreground">{p.sku}</span>}
                            {p.manual && <Badge color="bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">Manual</Badge>}
                            {boxes && boxes.length > 1 && <Badge color="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">{boxes.length} boxes</Badge>}
                            {p.shipped_on_pallet && <Badge color="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">Pallet</Badge>}
                            {p.fragile && <Badge color="bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">Fragile</Badge>}
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-2">
                            <div className="text-[11px] text-muted-foreground font-mono tabular-nums">
                              {boxes && boxes.length > 1
                                ? boxes.map((b, i) => <span key={i}>{i > 0 && ' + '}{b.width}×{b.height}×{b.length}</span>)
                                : <>{p.dimensions.width}×{p.dimensions.height}×{p.dimensions.length} cm</>}
                              <span className="mx-1.5 text-border">·</span>{(p.weight || 0).toFixed(2)} kg
                              <span className="mx-1.5 text-border">·</span>{fmtL(productVolume(p))} L
                            </div>
                            <div className="inline-flex items-center rounded-lg border border-border bg-background shrink-0">
                              <button onClick={() => updateQuantity(p.id, -1)} disabled={p.quantity <= 1} className="px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-40"><Minus className="h-3.5 w-3.5" /></button>
                              <input type="number" min="1" value={p.quantity} onChange={(e) => setQuantity(p.id, e.target.value)}
                                className="w-10 text-center font-semibold bg-transparent text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                              <button onClick={() => updateQuantity(p.id, 1)} className="px-2 py-1 text-muted-foreground hover:text-foreground"><Plus className="h-3.5 w-3.5" /></button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Live totals */}
            <div className="border-t border-border bg-card px-5 py-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { label: 'Products', value: products.length, icon: Package },
                  { label: 'Parcels', value: totalItems, icon: Box },
                  { label: 'Weight', value: `${totalWeight.toFixed(1)}kg`, icon: Scale },
                  { label: 'Volume', value: `${fmtL(totalVolume)}L`, icon: Layers },
                ].map(s => (
                  <div key={s.label} className="rounded-lg bg-muted/40 py-2">
                    <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><s.icon className="h-3 w-3" />{s.label}</div>
                    <div className="text-sm font-bold tabular-nums mt-0.5">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-3.5 border-t border-border bg-muted/30">
          <p className="text-xs text-muted-foreground">
            {!selectedCountry ? 'Pick a destination to simulate' : products.length === 0 ? 'Add at least one product' : <span className="text-foreground font-medium">Ready to simulate</span>}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSimulate} disabled={!ready} className="gap-2"><Sparkles className="h-4 w-4" /> Simulate Packing</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomOrderModal;
